import { Component, createRef } from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import Form, {
  GroupItem,
  SimpleItem,
  Label,
  RequiredRule,
  PatternRule,
  Item,
  EmptyItem
} from 'devextreme-react/form';
import { Accordion, FileUploader } from 'devextreme-react';
import { confirm } from 'devextreme/ui/dialog';
import DxForm from "devextreme/ui/form";
import {
  DataGrid,
  Column,
  Editing,
  Lookup,
  Paging,
} from 'devextreme-react/data-grid';
import { LoadPanel } from 'devextreme-react/load-panel';
import { Popup } from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';
import { DateBox } from 'devextreme-react/date-box';
import { Switch } from 'devextreme-react/switch';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import DataSource from 'devextreme/data/data_source';
import notify from 'devextreme/ui/notify';
import Tabs from 'devextreme-react/tabs';
import ScrollView from 'devextreme-react/scroll-view';
import SelectBox from 'devextreme-react/select-box';

import { FileDialogPopup } from './file-dialog-popup';
import { SchoolTitle } from './school-title';
import { SchoolItem } from './school-item';
import { CreateAuthorizedDataSource, CreateAuthorizedStore, SetAuthorizationHeaderAsync, getMemberById } from '~/helpers/data';
import { PersonCallLogGrid } from '~/components/person-call-log-grid/person-call-log-grid';
import { CommitteeGrid } from '~/components/committee-grid/committee-grid';
import { appConst } from '~/app-const';
import {
  calcGridHeight,
  setPreferredPhoneNumber, setPreferredEmailAddress,
  cancelEditFormData,
  setDateInlineEditing,
  updateAddressFields,
  setFocusOnEditor,
  compareAddresses,
  getStoredAddress,
  setStoredAddress,
  getEditPersonFormTitle,
} from '~/helpers/ui';
import { AddressAutoComplete } from '~/components/address-auto-complete/address-auto-complete';
import { ExternalMailSystem } from '~/components/external-mail-system/external-mail-system';
import { SaveAndCancelButtons } from '~/components/save-and-cancel-buttons/save-and-cancel-buttons';

import '../pages.scss';
import './edit-member-form.scss';

const URL = `${window.env.apiEndpoint}/api/v1/Members`;
const URL2 = `${window.env.apiEndpoint}/api/v1/MemberFunds`;

const mimeTypeFromFileName = (fileName) => {
  const extension = fileName?.split('.').pop()?.toLowerCase();
  return appConst.mimeTypesByExtension[extension];
};

const fundTypesData = CreateAuthorizedDataSource({ loadUrl: `${window.env.apiEndpoint}/api/v1/FundTypes/FundTypesLookup` }, null, 'Value', null, 'Header');

const tabs = [
  {
    id: 0,
    text: "Member's Information",
    icon: 'user',
  },
  {
    id: 1,
    text: 'School Associations',
    icon: 'fas fa-building',
  },
  {
    id: 2,
    text: 'Committees',
    icon: 'group',
  },
  {
    id: 3,
    text: 'Attachments',
    icon: 'file',
  },
  {
    id: 4,
    text: 'Funding',
    icon: 'money',
  },
  {
    id: 5,
    text: 'Calls',
    icon: 'tel',
  },
  {
    id: 6,
    text: 'Involved Calls',
    icon: 'tel',
  },
];

export class EditMemberForm extends Component {
  addPhoneButtonOptions = {
    icon: 'add',
    text: 'Add Phone',
    onClick: () => {
      const { rowData } = this.state;
      const tmpState = { ...rowData };
      tmpState.PhoneNumbers.push({ Phone: '', Preferred: (tmpState.PhoneNumbers && !tmpState.PhoneNumbers.length) });
      this.setState({ rowData: tmpState },
        () => {
          setFocusOnEditor(this.formRef.current.instance(), `PhoneNumbers[${tmpState.PhoneNumbers.length - 1}].Number`);
        }
      );
    },
  }

  addEmailButtonOptions = {
    icon: 'add',
    text: 'Add Email',
    onClick: () => {
      const { rowData } = this.state;
      const tmpState = { ...rowData };
      tmpState.EmailAddresses.push({ Address: '', Preferred: (tmpState.EmailAddresses && !tmpState.EmailAddresses.length), HasError: false});
      this.setState({ rowData: tmpState },
        () => {
          setFocusOnEditor(this.formRef.current.instance(), `EmailAddresses[${tmpState.EmailAddresses.length - 1}].Address`);
        }
      );
    },
  }

  constructor(props) {
    super(props);

    // The school accordion title component.
    this.renderSchoolAccordionTitle = (itemData, index) => (
      <SchoolTitle
        dataForm={itemData}
        removeSchool={this.removeSchool}
        index={index}
      />
    );

    // The school accordion item component.
    this.renderSchoolAccordionItem = (itemData) => (
      <SchoolItem
        dataForm={itemData}
        removeCSR={this.removeCSR}
        addCSRRole={this.addCSRRole}
      />
    );

    // I store the rowData passed from the members view in the state.
    // or instead we can call an api to get data for the edited member.
    const { rowData, provincesData, addNewMode, selectedTabIndex } = this.props;
    const province = provincesData && provincesData.find(p => p.Value === rowData.ProvinceId);
    const now = new Date();
    this.state = {
      rowData,
      oldRowData: JSON.stringify(rowData),
      Schools: rowData.Schools,
      selectedTabIndex: selectedTabIndex ? selectedTabIndex : 0,
      showFileNameDialogForConfidential: false,
      showFileNameDialog: false,
      newFileName: null,
      city: rowData.City,
      province: province && province.Text,
      storedAddress: getStoredAddress(rowData),
      addNewMode: addNewMode,
      showLoadingPanel: false,
      needToRefreshTheGridOnCancel: false,
      showChangeEmploymentStatusConfirmPopup: false,
      previousEmploymentStatusId: 0,
      changedEmploymentStatusEffectiveDate: now,
    };

    this.removeSchool = this.removeSchool.bind(this);
    this.removeCSR = this.removeCSR.bind(this);
    this.addCSRRole = this.addCSRRole.bind(this);

    // this datasource is used to retrieve and delete confidential attachments from
    // azure storage blob.

    this
      .attachmentsGridOnRowRemoving = this
        .attachmentsGridOnRowRemoving.bind(this);
    this.onTabsSelectionChanged = this.onTabsSelectionChanged.bind(this);
    this.saveData = this.saveData.bind(this);
    this.cancelData = this.cancelData.bind(this);
    this.downloadAttachment = this.downloadAttachment.bind(this);

    this.getTitle = this.getTitle.bind(this);
    this.onFileUploaded = this.onFileUploaded.bind(this);
    this.setReplaceValue = this.setReplaceValue.bind(this);
    this.hideFileDialogPopup = this.hideFileDialogPopup.bind(this);
    this.selectBoxFundTypeColumnTemplate = this.selectBoxFundTypeColumnTemplate.bind(this);
    this.setGridHeights = this.setGridHeights.bind(this);
    this.updateAddressFields = this.updateAddressFields.bind(this);
    this.setAddressVerified = this.setAddressVerified.bind(this);
    this.repaintForm = this.repaintForm.bind(this);
    this._saving = false;

    this.formRef = createRef();
    this.schoolsAccordionRef = createRef();
    this.fundsGridRef = createRef();
    this.personCallLogGridRef = createRef();
    this.involvedCallsGridRef = createRef();
    this.committeeGridRef = createRef();
    this.formScrollViewRef = createRef();
    this.confidentialAttachmentUploadFileRef = createRef(); 
    this.confidentialAttachmentsGridRef = createRef();
    this.attachmentsGridRef = createRef();
    this.attachmentUploadFileRef = createRef();
  }

  // this datasource is used to retrieve,update and insert member's fund
  fundsDataSource = () => {
    const { rowData: { Id } } = this.state;
    return CreateAuthorizedDataSource(
      {
        loadUrl: `${URL2}/GetFundsByMember`,
        insertUrl: `${URL2}/Post`,
        updateUrl: `${URL2}/Put`,
        deleteUrl: `${URL2}/Delete`,
      }, { load: { memberId: Id }, insert: { memberId: Id } }, 'Id', false,
    );
  }

  confidentialAttachmentDataSource = () => {
    const { Id } = this.state.rowData;
    return new DataSource({
      store: createStore({
        loadUrl: `${URL}/ListConfidentialAttachments?Id=${Id}`,
        key: 'Id',
        deleteUrl: `${URL}/DeleteConfidentialAttachment`,
        onBeforeSend: async (method, ajaxOptions) => {
          await SetAuthorizationHeaderAsync(ajaxOptions);

          const { AttachmentName } = this.state;
          if (method === 'delete' && AttachmentName) {
            ajaxOptions.data.fileName = AttachmentName;
            ajaxOptions.data.memberId = Id;
          }
        },
      }),
      paginate: false,
    });
  }

  // this datasource is used to retrieve and delete regular attachments from
  // azure storage blob.
  attachmentDataSource = () => {
    const { Id } = this.state.rowData;
    return new DataSource({
      store: createStore({
        loadUrl: `${URL}/ListAttachments?Id=${Id}`,
        key: 'Id',
        deleteUrl: `${URL}/DeleteAttachment`,
        onBeforeSend: async (method, ajaxOptions) => {
          await SetAuthorizationHeaderAsync(ajaxOptions);

          const { AttachmentName } = this.state;
          if (method === 'delete' && AttachmentName) {
            ajaxOptions.data.fileName = AttachmentName;
            ajaxOptions.data.memberId = Id;
          }
        },
      }),
      paginate: false,
    });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setGridHeights);
  }

  componentDidMount() {
    window.addEventListener('resize', this.setGridHeights);
    this.props.updateTitle(this.getTitle());
  }

  // this button options are used for the remove email button
  // the index points the order of remove button.
  removeEmailButtonOptions = (index) => ({
    icon: 'trash',
    onClick: () => {
      const result = confirm(appConst.confirmDialogContent, appConst.confirmDeleteDialogTitle);
      result.then((dialogResult) => {
        if (dialogResult) {
          const { rowData } = this.state;
          const tmpState = { ...rowData };
          const i = parseInt(index, 10);
          tmpState.EmailAddresses.splice(i, 1);
          this.setState({ rowData: tmpState });
        }
      });
    },
  })

  async repaintForm() {
    this.formRef.current.instance().repaint();
    //we want to delay slightly to allow the DOM/layout to settle
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  // this button options are used for the remove phone button
  // the index points the order of remove button.
  removePhoneButtonOptions = (index) => ({
    icon: 'trash',
    onClick: () => {
      const result = confirm(appConst.confirmDialogContent, appConst.confirmDeleteDialogTitle);
      result.then((dialogResult) => {
        if (dialogResult) {
          const { rowData } = this.state;
          const tmpState = { ...rowData };
          const i = parseInt(index, 10);
          tmpState.PhoneNumbers.splice(i, 1);
          this.setState({ rowData: tmpState });
        }
      });
    },
  })

  removeDesignationOptions = (index) => ({
    icon: 'trash',
    onClick: () => {
      const result = confirm(appConst.confirmDialogContent, appConst.confirmDeleteDialogTitle);
      result.then((dialogResult) => {
        if (dialogResult) {
          const { rowData } = this.state;
          const tmpState = { ...rowData };
          const i = parseInt(index, 10);
          tmpState.Designations.splice(i, 1);
          this.setState({ rowData: tmpState }, async () => {
            const sv = this.formScrollViewRef.current.instance();

            if (tmpState.Designations.length === 0) {
              await this.repaintForm();
            }
            sv.scrollTo({ top: sv.scrollHeight() });
          });
        }
      });
    },
  })

  addDesignationButtonOptions = {
    icon: 'add',
    text: 'Add Designation',
    onClick: () => {
      const { rowData } = this.state;
      const tmpState = { ...rowData };
      if (typeof tmpState.Designations === 'undefined') { tmpState.Designations = []; }
      tmpState.Designations.push({});
      this.setState({ rowData: tmpState }, () => {
        setFocusOnEditor(this.formRef.current.instance(), `Designations[${tmpState.Designations.length - 1}].DesignationId`);

        const sv = this.formScrollViewRef.current.instance();
        sv.scrollTo({ top: sv.scrollHeight() });
      });
    },
  }

  addSchoolButtonOptions = {
    text: 'Add Associated School',
    onClick: () => {
      const { Schools } = this.state;
      const tmpState = [...Schools];
      tmpState.push({ SchoolName: 'New School', Index: tmpState.length });
      this.setState({ Schools: tmpState });
      this.schoolsAccordionRef.current.instance().option('selectedIndex', tmpState.length - 1);
    },
  }

  onTabsSelectionChanged(args) {
    if (args.name === 'selectedIndex') {
      const { addNewMode } = this.state;
      if (addNewMode && args.value > 1) {
        notify('You are not allowed to add attachments, committees, funding or calls unless member is saved.');
      } else {
        this.setState({
          selectedTabIndex: args.value,
        });
      }
    }

    if (args.name === 'isActive') {
      const { selectedTabIndex } = this.state;

      if ([2, 4, 5, 6].includes(selectedTabIndex)) {
        this.setGridHeights();
      }

      if (0 === selectedTabIndex) {
        setFocusOnEditor(this.formRef.current.instance(), "FirstName");
      }
    }
  }

  setGridHeights() {
    this.fundsGridRef.current.instance().option('height', calcGridHeight('fundsGrid'));
    this.personCallLogGridRef.current.setGridHeight();
    this.committeeGridRef.current.setGridHeight();
    this.involvedCallsGridRef.current.setGridHeight();
  }

  attachmentsGridOnRowRemoving(e) {
    this.setState({ AttachmentName: e.data.Name });
  }

  setAddressVerified(value) {
    const { rowData } = this.state;
    this.setState({
      rowData: { ...rowData, AddressVerified: value },
    });
  }

  async getMemberByIdAsync(Id) {
    try {
      const member = await getMemberById(Id);
      return member;
    }
    catch (error) {
      notify({ message: error.message, shading: true }, 'error');
    }
  }

  async afterSave(closePopup, Id) {
    if (!closePopup) {
      const member = await this.getMemberByIdAsync(Id);
      if (typeof member === "undefined") return;
      this.setState({
        oldRowData: JSON.stringify(member),
        rowData: member,
        addNewMode: false,
        Schools: member.Schools,
        showLoadingPanel: false,
        needToRefreshTheGridOnCancel: true,
      }, () => { return });
    } else {
      const { hideEditPopupAndRefreshGrid } = this.props;
      hideEditPopupAndRefreshGrid();
    }
  }

  saveData(closePopup) {
    if (this._saving) return;
    const { Schools, addNewMode, rowData, changedEmploymentStatusEffectiveDate } = this.state;

    const v = this.formRef.current.instance().validate();
    if (!v.isValid) return false;
    const schoolItems = this.schoolsAccordionRef.current.instance().element().querySelectorAll("#schoolForm");
    let validSchools = true;
    for (const element of schoolItems) {
      let f = DxForm.getInstance(element);
      if (typeof f !== "undefined") {
        if (!f.validate().isValid) {
          validSchools = false;
        }
      }
    }
    if (!validSchools) return;
    this._saving = true;
    this.setState({ showLoadingPanel: true });
    const data = rowData;
    data.Schools = Schools;
    data.City = data.City && data.City.trim();
    data.Address1 = data.Address1 && data.Address1.trim();
    const saveMemberData = CreateAuthorizedStore(
      { updateUrl: `${URL}/Put`, insertUrl: `${URL}/Post` },
      {
        update: { effectiveDate: changedEmploymentStatusEffectiveDate && dayjs(changedEmploymentStatusEffectiveDate).format('YYYY-MM-DD') },
        insert: { effectiveDate: changedEmploymentStatusEffectiveDate && dayjs(changedEmploymentStatusEffectiveDate).format('YYYY-MM-DD') }
      }, 'Id',
    );
    if (!addNewMode) {
      saveMemberData.update(data.Id, data).then(
        async () => {
          this._saving = false;
          await this.afterSave(closePopup, data.Id, this);
        },
        (error) => {
          this._saving = false;
          this.setState({ showLoadingPanel: false });
          notify({ message: error.message, shading: true }, 'error');
        },
      );
    } else {
      saveMemberData.insert(data).then(
        async (result) => {
          this._saving = false;
          await this.afterSave(closePopup, result, this);
        },
        (error) => {
          this._saving = false;
          this.setState({ showLoadingPanel: false });
          notify({ message: error.message, shading: true }, 'error');
        },
      );
    }
  }

  cancelData() {
    const { hideEditPopupAndRefreshGrid, hideEditPopup } = this.props;
    const { Schools, oldRowData, needToRefreshTheGridOnCancel } = this.state;
    const editedData = this.formRef.current.instance().option('formData');
    editedData.Schools = Schools;
    if (needToRefreshTheGridOnCancel)
      cancelEditFormData(oldRowData, JSON.stringify(editedData), this.saveData, hideEditPopupAndRefreshGrid);
    else
      cancelEditFormData(oldRowData, JSON.stringify(editedData), this.saveData, hideEditPopup);
  }

  async downloadAttachment(fileName) {
    try {
      const o = {};
      await SetAuthorizationHeaderAsync(o);
      const response = await fetch(`${URL}/DownloadAttachment?fileName=${encodeURIComponent(fileName)}`, { headers: o.headers });
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }
      // Files are stored in blob storage as application/octet-stream, so the
      // response carries no usable MIME type. Re-wrap the bytes with the type
      // inferred from the extension so the browser can render it inline.
      const rawBlob = await response.blob();
      const contentType = mimeTypeFromFileName(fileName) || rawBlob.type || 'application/octet-stream';
      const blob = new Blob([rawBlob], { type: contentType });
      // Build a blob: URL (https://developer.mozilla.org/en-US/docs/Web/URI/Reference/Schemes/blob)
      // so the authenticated, in-memory file can be opened in a new tab.
      const objectUrl = window.URL.createObjectURL(blob);
      window.open(objectUrl, '_blank', 'noopener noreferrer');
      // Revoke once the new tab has had time to load it — revoking immediately
      // would pull the resource out from under the just-opened tab.
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
    }
    catch (error) {
      notify({ message: error.message, shading: true }, 'error');
    }
  }

  getTitle() {
    const { addNewMode, rowData } = this.state;
    return getEditPersonFormTitle(rowData, addNewMode, "Member");
  }

  onFileUploaded(gridRef, uploadRef, fileName) {
    gridRef.refresh();
    uploadRef.reset();
    notify(`File ${fileName} has been uploaded.`);
    this.setState({
      showFileNameDialogForConfidential: false, showFileNameDialog: false, newFileName: null, file: null, uploadRef: null,
    });
  }

  setReplaceValue(value) {
    this.confidentialAttachmentUploadFileRef.current.instance().option("uploadUrl", `${URL}/UploadConfidentialAttachment?Id=${this.state.rowData.Id}&replace=${value}`);
    this.attachmentUploadFileRef.current.instance().option("uploadUrl", `${URL}/UploadAttachment?Id=${this.state.rowData.Id}&replace=${value}`)
  }

  hideFileDialogPopup() {
    this.setState({
      showFileNameDialogForConfidential: false, showFileNameDialog: false, newFileName: null, file: null, uploadRef: null, gridRef: null,
    });
  }

  // Handles the user's choice from the duplicate-file dialog by re-driving the
  // DevExtreme FileUploader (rename -> upload a renamed copy; replace -> upload
  // with replace=true; cancel -> reset).
  resolveAttachmentConflict(result, gridInstance, uploadInstance) {
    if (result.action === 'cancel') {
      uploadInstance.reset();
      this.hideFileDialogPopup();
      return;
    }

    const { file, newFileName } = this.state;
    let newFile;
    let uploadedName;
    if (result.action === 'rename') {
      newFile = new File([file], result.newName);
      uploadedName = result.newName;
      this.setReplaceValue(false);
    } else {
      newFile = file;
      uploadedName = newFileName;
      this.setReplaceValue(true);
    }

    uploadInstance.onFileUploaded = () => {
      this.onFileUploaded(gridInstance, uploadInstance, uploadedName);
    };
    uploadInstance._changeValue([newFile]);
    uploadInstance._uploadFiles();
  }

  onSelectBoxFundTypeValueChanged(cell, e) {
    cell.setValue(e.value);
  }

  selectBoxFundTypeColumnTemplate(cell) {
    const onValueChanged = this.onSelectBoxFundTypeValueChanged.bind(this, cell);
    return (
      <SelectBox
        {...appConst.defaultSelectBoxOptions}
        defaultValue={cell.value}
        {...cell.column.lookup}
        onValueChanged={onValueChanged}
        itemRender={this.itemRender}
        grouped
        dataSource={fundTypesData}
      />
    );
  }

  onEditorPreparingFundsGrid(e) {
    setDateInlineEditing(['FundDate'], e);
  }

  updateAddressFields(addressObj) {
    const { provincesData } = this.props;
    const { rowData } = this.state;

    this.setState({ rowData: updateAddressFields(addressObj, rowData, provincesData) },
      () => this.setState({ storedAddress: setStoredAddress(addressObj) }));
  }

  toolbarButtons() {
    return <SaveAndCancelButtons saveData={this.saveData} cancelData={this.cancelData} />;
  }

 renderShowPopup() {
  return (
    <Popup
      visible={this.state.showChangeEmploymentStatusConfirmPopup}
      width="25%"
      height="25%"
      onHiding={() => { this.setState({ showChangeEmploymentStatusConfirmPopup: false }); }}
      title="Select effective date..."
      showCloseButton={false}
    >
      <div>
        <i>Changing the employment status to {this.formRef && this.formRef.current.instance() && this.formRef.current.instance().getEditor("EmploymentStatusId").option("text")} will change other properties automatically</i>
      </div>
      <DateBox
        defaultValue={new Date()}
        type="date"
        {...appConst.defaultSelectDateBoxOptions}
        onValueChanged={(e) => {
          this.setState({ changedEmploymentStatusEffectiveDate: e.value });
        }}
        dateSerializationFormat="yyyy-MM-dd"
      />
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop:'20px'
      }} >
        <Button
          text='Ok'
          width={150}
          onClick={
            () => {
              if (!this.state.changedEmploymentStatusEffectiveDate) {
                notify({ message: "Effective date can't be empty", shading: true }, 'error');
              } else {
                this.setState({ showChangeEmploymentStatusConfirmPopup: false });
              }
            }
          }
        />
        <Button
          text='Cancel'
          width={150}
          onClick={
            () => {
              this.setState({ showChangeEmploymentStatusConfirmPopup: false });
              this.formRef.current.instance().getEditor("EmploymentStatusId").option("value", this.state.previousEmploymentStatusId);
              this.setState({ changedEmploymentStatusEffectiveDate: null});
            }
          }
        />
      </div>
    </Popup>)
}

  render() {
    const {
      membershipTypesData, employmentStatusesData, contractTypesData, phoneTypesData,
      emailAddressTypesData, salutationsData, gendersData, pronounsData, designationsData, ridingsData, provincesData
    } = this.props;
    const {
      selectedTabIndex, Schools, rowData, rowData: { Designations }, city, province, storedAddress, showLoadingPanel, addNewMode, showChangeEmploymentStatusConfirmPopup
    } = this.state;
    return (
      <>
        {showChangeEmploymentStatusConfirmPopup && this.renderShowPopup()}
        <LoadPanel
          shadingColor="rgba(0,0,0,0.4)"
          position="center"
          visible={showLoadingPanel}
        />
        <Tabs
          dataSource={tabs}
          selectedIndex={selectedTabIndex}
          onOptionChanged={this.onTabsSelectionChanged}
        />
        <br />
        <div className={(selectedTabIndex === 0) ? 'tab-form-container-visible' : 'tab-container-invisible'}>
          <ScrollView ref={this.formScrollViewRef}>
            <Form
              ref={this.formRef}
              scrollingEnabled={false}
              formData={rowData}
              {...appConst.defaultFormOptions}
            >
              <GroupItem caption="Personal Information" colCount={4}>
                <SimpleItem
                  dataField="FirstName"
                  editorType="dxTextBox"
                  editorOptions={{
                    valueChangeEvent: 'keyup',
                    onValueChanged: () => {
                      this.props.updateTitle(this.getTitle());
                    },
                  }}
                >
                  <RequiredRule message="First name is required" />
                </SimpleItem>
                <SimpleItem
                  dataField="LastName"
                  editorOptions={{
                    valueChangeEvent: "keyup",
                    onValueChanged: () => {
                      this.props.updateTitle(this.getTitle());
                    },
                  }}
                />
                <SimpleItem
                  dataField="PreferredFirstName"
                  editorOptions={{
                    valueChangeEvent: "keyup",
                    onValueChanged: (e) => {
                      if (typeof e.event === "undefined" && e.value === null) return;
                      this.props.updateTitle(this.getTitle());
                    },
                  }}
                />
                <SimpleItem dataField="PreviousLastName" />
                <SimpleItem dataField="PreviousFirstName" />
                <SimpleItem
                  editorType="dxSelectBox"
                  dataField="SalutationId"
                  editorOptions={{ dataSource: salutationsData, ...appConst.defaultSelectBoxOptions }}
                >
                  <Label text="Salutation" />
                </SimpleItem>
                <SimpleItem
                  editorType="dxSelectBox"
                  dataField="GenderId"
                  editorOptions={{ dataSource: gendersData, ...appConst.defaultSelectBoxOptions }}
                >
                  <Label text="Gender" />
                </SimpleItem>
                <SimpleItem
                  editorType="dxSelectBox"
                  dataField="PronounsId"
                  editorOptions={{ dataSource: pronounsData, ...appConst.defaultSelectBoxOptions }}
                >
                  <Label text="Pronouns" />
                </SimpleItem>
                <SimpleItem
                  editorType="dxDateBox"
                  dataField="BirthDate"
                  editorOptions={{ ...appConst.defaultSelectDateBoxOptions }}
                />
                <SimpleItem
                  editorType="dxDateBox"
                  dataField="DateVaccinationRecordVerified"
                  editorOptions={{ ...appConst.defaultSelectDateBoxOptions }}
                >
                  <Label text="Vaccination Verified Date" />
                </SimpleItem>
              </GroupItem>
              <GroupItem caption="Contact Information" colCount={6}>
                <Item colSpan={3}>
                  <Label text="Address1" />
                  <div style={{ position: 'relative', width: '100%' }}>
                    <AddressAutoComplete
                      autoCompleteTextBoxId={"autoCompleteTextBoxId"}
                      updateAddressFields={this.updateAddressFields}
                      value={rowData && rowData.Address1}
                      onValueChanged={(value) => {
                        this.setState({ rowData: { ...rowData, Address1: value } });
                      }}
                      city={city}
                      province={province}
                      addressVerified={rowData.AddressVerified}
                      setAddressVerified={this.setAddressVerified}
                    />
                  </div>
                </Item>
                <SimpleItem dataField="City" editorOptions={{
                  ...appConst.defaultAddressInput,
                  onValueChanged: (e) => {
                    if (typeof e.event === "undefined" && e.value === null) return;
                    this.setState({ city: e.value.trim() }, () => compareAddresses(e, rowData, storedAddress, this.setAddressVerified));
                  },
                }} />
                <SimpleItem
                  editorType="dxSelectBox"
                  dataField="ProvinceId"
                  editorOptions={{
                    dataSource: provincesData, ...appConst.defaultSelectBoxOptions,
                    grouped: false,
                    onValueChanged: (e) => {
                      if (typeof e.event === "undefined" && e.value === null) return;
                      this.setState({ province: e.component.option('text') }, () => compareAddresses(e, rowData, storedAddress, this.setAddressVerified));
                    },
                  }}
                >
                  <Label text="Province" />
                </SimpleItem>
                <SimpleItem dataField="PostalCode" editorType="dxTextBox"
                  editorOptions={{
                    ...appConst.defaultAddressInput,
                    onValueChanged: (e) => {
                      if (typeof e.event === "undefined" && e.value === null) return;
                      compareAddresses(e, rowData, storedAddress, this.setAddressVerified);
                    },
                  }}>
                  <PatternRule
                    pattern={appConst.postalCodePattern}
                    message={appConst.postalCodeValidationMessage}
                  />
                </SimpleItem>
                <SimpleItem
                  dataField="Ward"
                >
                  <Label
                    text="Ward"
                  />
                  <PatternRule
                    pattern={appConst.schoolWardPattern}
                    message={appConst.schoolWardValidationMessage}
                  />
                </SimpleItem>
                <SimpleItem
                  editorType="dxSelectBox"
                  dataField="RidingId"
                  editorOptions={
                    {
                      dataSource: ridingsData,
                      ...appConst.defaultSelectBoxOptions,
                    }
                  }
                >
                  <Label text="Riding" />
                </SimpleItem>
                <EmptyItem />
                <EmptyItem />
              </GroupItem>
              <GroupItem name="EmailsAndPhones" colCount={2}>
                <GroupItem name="Emails" colCount={1}>
                  {(selectedTabIndex === 0) && rowData.EmailAddresses && rowData.EmailAddresses.map((email, index) => (
                    <GroupItem key={`EmailGroup ${index + 1}`} colCount={12}>
                      <SimpleItem
                        key={`Emails${email.Id}`}
                        colSpan={5}
                        dataField={`EmailAddresses[${index}].Address`}
                      >
                        <Label text={`Email ${index + 1}`} />
                        <RequiredRule />
                        <PatternRule
                          pattern={appConst.emailPattern}
                          message={appConst.emailValidationMessage}
                        />
                      </SimpleItem>
                      <SimpleItem
                        key={`PreferredEmails${email.Id}`}
                        colSpan={2}
                        dataField={`EmailAddresses[${index}].Preferred`}
                        editorType="dxSwitch"
                        verticalAlignment="bottom"
                        editorOptions={{
                          onValueChanged: () => {
                            this.formRef && this.formRef.current
                              && setPreferredEmailAddress(index, this.formRef.current.instance());
                          },
                        }}
                      >
                        <Label text="Preferred" />
                      </SimpleItem>
                      <SimpleItem
                        key={`EmailTypes${email.Id}`}
                        colSpan={3}
                        editorType="dxSelectBox"
                        dataField={`EmailAddresses[${index}].EmailTypeId`}
                        editorOptions={
                          {
                            dataSource: emailAddressTypesData,
                            ...appConst.defaultSelectBoxOptions,
                            grouped: false,
                          }
                        }
                      >
                        <Label text={`Email Type ${index + 1}`} />
                      </SimpleItem>
                      {!addNewMode && <SimpleItem colSpan={1}>
                        <ExternalMailSystem
                          statusMessage={email.StatusMessage} hasError={email.HasError}
                          addNewMode={addNewMode} entityId={email.EmailAddressId} entityType="EmailAddress" />
                      </SimpleItem>}
                      <SimpleItem
                        colSpan={1}
                        key={`EmailRemoveButton${email.Id}`}
                        itemType="button"
                        horizontalAlignment="left"
                        verticalAlignment="bottom"
                        buttonOptions={{ ...this.removeEmailButtonOptions(`${index}`) }}
                      />
                    </GroupItem>
                  ))}
                  <SimpleItem
                    itemType="button"
                    horizontalAlignment="left"
                    buttonOptions={this.addEmailButtonOptions}
                  />
                </GroupItem>
                <GroupItem name="Phones" colCount={1}>
                  {(selectedTabIndex === 0) && rowData.PhoneNumbers && rowData.PhoneNumbers.map((phone, index) => (
                    <GroupItem key={`phoneGroup ${index + 1}`} colCount={6}>
                      <SimpleItem
                        key={`Phones${phone.Id}`}
                        colSpan={2}
                        dataField={`PhoneNumbers[${index}].Number`}
                        editorOptions={appConst.phoneEditorOptions}
                      >
                        <Label text={`Phone ${index + 1}`} />
                        <RequiredRule />
                        <PatternRule
                          pattern={appConst.phone10DigitsPattern}
                          message={appConst.phoneEditorOptions.message}
                        />
                      </SimpleItem>
                      <SimpleItem
                        key={`PreferredPhones${phone.Id}`}
                        dataField={`PhoneNumbers[${index}].Preferred`}
                        editorType="dxSwitch"
                        verticalAlignment="bottom"
                        editorOptions={{
                          onValueChanged: () => {
                            this.formRef && this.formRef.current
                              && setPreferredPhoneNumber(index, this.formRef.current.instance());
                          },
                        }}
                      >
                        <Label text="Preferred" />
                      </SimpleItem>
                      <SimpleItem
                        key={`PhoneTypes${phone.Id}`}
                        colSpan={2}
                        editorType="dxSelectBox"
                        dataField={`PhoneNumbers[${index}].PhoneTypeId`}
                        editorOptions={
                          {
                            dataSource: phoneTypesData,
                            ...appConst.defaultSelectBoxOptions,
                            grouped: false,
                          }
                        }
                      >
                        <Label text={`Phone Type ${index + 1}`} />
                      </SimpleItem>
                      <SimpleItem
                        key={`PhoneRemoveButton${phone.Id}`}
                        itemType="button"
                        horizontalAlignment="left"
                        verticalAlignment="bottom"
                        buttonOptions={this.removePhoneButtonOptions(`${index}`)}
                      />
                    </GroupItem>
                  ))}
                  <SimpleItem
                    itemType="button"
                    horizontalAlignment="left"
                    buttonOptions={this.addPhoneButtonOptions}
                  />
                </GroupItem>
              </GroupItem>
              <GroupItem caption="Membership and Employment Information" colCount={5}>
                <SimpleItem
                  editorType="dxDateBox"
                  dataField="StartDate"
                  editorOptions={{ ...appConst.defaultSelectDateBoxOptions }}
                >
                  <Label text="Start Date" />
                </SimpleItem>
                <SimpleItem editorType="dxSelectBox" dataField="MembershipTypeId" editorOptions={{ dataSource: membershipTypesData, ...appConst.defaultSelectBoxOptions }}>
                  <Label text="Membership Type" />
                </SimpleItem>
                <SimpleItem editorType="dxSelectBox" dataField="EmploymentStatusId" editorOptions={{
                  dataSource: employmentStatusesData,
                  ...appConst.defaultSelectBoxOptions,
                  onValueChanged: (e) => {
                    if (typeof e.event === 'undefined') return;

                    const selectedItem = e.component.option("selectedItem");
                    if (selectedItem.Confirm) {
                      this.setState({ showChangeEmploymentStatusConfirmPopup: true, previousEmploymentStatusId: e.previousValue })
                    }
                  }
                }}>
                  <Label text="Employment Status" />
                </SimpleItem>
                <SimpleItem editorType="dxSelectBox" dataField="ContractTypeId" editorOptions={{
                  dataSource: contractTypesData,
                  ...appConst.defaultSelectBoxOptions,
                }}>
                  <Label text="Contract Type" />
                </SimpleItem>
                <SimpleItem editorType="dxTextBox" dataField="CertificateNumber">
                  <Label text="CertificateNumber" />
                </SimpleItem>
                <SimpleItem
                  editorType="dxDateBox"
                  dataField="DateInducted"
                  editorOptions={{ ...appConst.defaultSelectDateBoxOptions }}
                >
                  <Label text="Date Inducted" />
                </SimpleItem>
                <SimpleItem
                  editorType="dxTextBox"
                  dataField="EmployeeId"
                >
                  <Label text="Employee ID" />
                </SimpleItem>
                <SimpleItem
                  editorType="dxTextBox"
                  dataField="EpsId"
                >
                  <Label text="EPS ID" />
                </SimpleItem>
                <SimpleItem
                  editorType="dxTextBox"
                  dataField="ImportId"
                  editorOptions={{ readOnly: true }}
                >
                  <Label text="Import Id" />
                </SimpleItem>
              </GroupItem>
              <GroupItem name="Designations" colCount={1} caption="Designations">
                {Designations && Designations.map((designation, index) => (
                  <GroupItem key={`DesignationGroup ${index + 1}`} colCount={10}>
                    <SimpleItem
                      colSpan={2}
                      key={`Designation${designation.Id}`}
                      editorType="dxSelectBox"
                      dataField={`Designations[${index}].DesignationId`}
                      editorOptions={{
                        dataSource: designationsData,
                        ...appConst.defaultSelectBoxOptions,
                        grouped: true,
                      }}
                    >
                      <Label text={`Designation ${index + 1}`} />
                      <RequiredRule message="Designation is required" />
                    </SimpleItem>
                    <SimpleItem
                      colSpan={2}
                      key={`DesignationStartDate${designation.Id}`}
                      editorType="dxDateBox"
                      editorOptions={{ ...appConst.defaultSelectDateBoxOptions }}
                      dataField={`Designations[${index}].StartDate`}
                    >
                      <Label text="Start Date" />
                    </SimpleItem>
                    <SimpleItem
                      colSpan={2}
                      key={`DesignationEndDate${designation.Id}`}
                      editorType="dxDateBox"
                      editorOptions={{ ...appConst.defaultSelectDateBoxOptions }}
                      dataField={`Designations[${index}].EndDate`}
                    >
                      <Label text="End Date" />
                    </SimpleItem>
                    <SimpleItem
                      colSpan={3}
                      key={`DesignationNotes${designation.Id}`}
                      editorType="dxTextBox"
                      dataField={`Designations[${index}].Notes`}
                    >
                      <Label text="Notes" />
                    </SimpleItem>
                    <SimpleItem
                      key={`DesignationRemoveButton${designation.Id}`}
                      itemType="button"
                      horizontalAlignment="left"
                      verticalAlignment="bottom"
                      buttonOptions={this.removeDesignationOptions(`${index}`)}
                    />
                  </GroupItem>
                ))}
              </GroupItem>
              <SimpleItem
                itemType="button"
                horizontalAlignment="left"
                buttonOptions={this.addDesignationButtonOptions}
              />
            </Form>
          </ScrollView>
          {this.toolbarButtons()}
        </div>
        <div className={(selectedTabIndex === 1) ? 'tab-form-container-visible' : 'tab-container-invisible'}>
          <ScrollView>
            <Form>
              <GroupItem name="Schools" colCount={1}>
                <Accordion
                  ref={this.schoolsAccordionRef}
                  name="schoolsAccordion"
                  dataSource={[...Schools]}
                  itemRender={this.renderSchoolAccordionItem}
                  itemTitleRender={this.renderSchoolAccordionTitle}
                  multiple={true}
                />
              </GroupItem>
              <GroupItem>
                <SimpleItem
                  itemType="button"
                  horizontalAlignment="left"
                  buttonOptions={this.addSchoolButtonOptions}
                />
              </GroupItem>
            </Form>
          </ScrollView>
          {this.toolbarButtons()}
        </div>
        <div className={(selectedTabIndex === 3) ? 'tab-form-container-visible' : 'tab-container-invisible'}>
          <FileDialogPopup
            visible={this.state.showFileNameDialogForConfidential}
            fileName={this.state.newFileName}
            fileDate={this.state.fileCreationDate}
            entityLabel="member"
            onResolve={(result) => this.resolveAttachmentConflict(
              result,
              this.confidentialAttachmentsGridRef.current.instance(),
              this.confidentialAttachmentUploadFileRef.current.instance(),
            )}
          />
          <FileDialogPopup
            visible={this.state.showFileNameDialog}
            fileName={this.state.newFileName}
            fileDate={this.state.fileCreationDate}
            entityLabel="member"
            onResolve={(result) => this.resolveAttachmentConflict(
              result,
              this.attachmentsGridRef.current.instance(),
              this.attachmentUploadFileRef.current.instance(),
            )}
          />
          <ScrollView className="tab-scrolling">
            <div className="attachment-type-title">Confidential Attachments</div>
            <FileUploader
              ref={this.confidentialAttachmentUploadFileRef}
              name="file"
              accept={appConst.acceptFileToUpload}
              uploadUrl={`${URL}/UploadConfidentialAttachment?Id=${rowData.Id}&replace=${false}`}
              uploadMode="instantly"
              onInitialized={async (e) => {
                const o = {};
                await SetAuthorizationHeaderAsync(o);
                e.component.option('uploadHeaders', o.headers);
              }}
              onUploaded={(e) => {
                this.onFileUploaded(this.confidentialAttachmentsGridRef.current.instance(), this.confidentialAttachmentUploadFileRef.current.instance(), e.file.name);
              }}
              onValueChanged={(e) => {
                if (e.value.length > 0) {
                  this.setState({
                    newFileName: e.value[0].name,
                    file: e.value[0],
                  });
                }
              }}
              onUploadError={(e) => {
                if (e.error.responseText.includes('FileExists')) {
                  const cd = e.error.responseText.split('|')[1];
                  this.setState({ showFileNameDialogForConfidential: true, fileCreationDate: cd });
                  e.request.abort();
                  e.component.reset();
                }
              }}
            />
            <DataGrid
              id="confidentialAttachmentsGrid"
              ref={this.confidentialAttachmentsGridRef}
              dataSource={this.confidentialAttachmentDataSource()}
              {...appConst.defaultAttachmentGridOptions}
              onRowRemoving={this.attachmentsGridOnRowRemoving}
              onRowDblClick={(e) => {
                this.downloadAttachment(e.data.Name);
              }}
            >
              <Paging
                enabled={false}
              />
              <Editing
                mode="Row"
                allowDeleting
              />
              <Column
                dataField="Name"
                dataType="string"
                cellRender={(data) => (
                  <a
                    href="#"
                    onClick={async (e) => {
                      e.preventDefault();
                      await this.downloadAttachment(data.row.data.Name);
                    }}
                  >
                    {data.row.data.FileName}
                  </a>
                )}
              />
              <Column dataField="DateUploaded" dataType="datetime" dateDisplayFormat={appConst.dateAndTimeDisplayFormat} />
              <Column dataField="Size" dataType="Number" />
            </DataGrid>
            <div className="attachment-type-title">Regular Attachments</div>
            <FileUploader
              ref={this.attachmentUploadFileRef}
              name="file"
              accept={appConst.acceptFileToUpload}
              uploadUrl={`${URL}/UploadAttachment?Id=${rowData.Id}&replace=${false}`}
              uploadMode="instantly"
              onInitialized={async (e) => {
                const o = {};
                await SetAuthorizationHeaderAsync(o);
                e.component.option('uploadHeaders', o.headers);
              }}
              onUploaded={(e) => {
                this.onFileUploaded(this.attachmentsGridRef.current.instance(), this.attachmentUploadFileRef.current.instance(), e.file.name);
              }}
              onValueChanged={(e) => {
                if (e.value.length > 0) {
                  this.setState({
                    newFileName: e.value[0].name,
                    file: e.value[0],
                  });
                }
              }}
              onUploadError={(e) => {
                if (e.error.responseText.includes('FileExists')) {
                  const cd = e.error.responseText.split('|')[1];
                  this.setState({ showFileNameDialog: true, fileCreationDate: cd });
                  e.request.abort();
                  e.component.reset();
                }
              }}
            />
            <DataGrid
              id="attachmentsGrid"
              ref={this.attachmentsGridRef}
              dataSource={this.attachmentDataSource()}
              {...appConst.defaultAttachmentGridOptions}
              onRowRemoving={this.attachmentsGridOnRowRemoving}
              onRowDblClick={(e) => {
                this.downloadAttachment(e.data.Name);
              }}
            >
              <Paging
                enabled={false}
              />
              <Editing
                mode="Row"
                allowDeleting
              />
              <Column
                dataField="Name"
                dataType="string"
                cellRender={(data) => (
                  <a
                    href="#"
                    onClick={async (e) => {
                      e.preventDefault();
                      await this.downloadAttachment(data.row.data.Name);
                    }}
                  >
                    {data.row.data.FileName}
                  </a>
                )}
              />
              <Column dataField="DateUploaded" dataType="datetime" dateDisplayFormat={appConst.dateAndTimeDisplayFormat} />
              <Column dataField="Size" dataType="Number" />
            </DataGrid>
          </ScrollView>
        </div>
        <div className={(selectedTabIndex === 2) ? 'tab-form-container-visible' : 'tab-container-invisible'}>
          <CommitteeGrid
            personId={rowData.Id}
            ref={this.committeeGridRef}
          />
        </div>
        <div className={(selectedTabIndex === 4) ? 'tab-form-container-visible' : 'tab-container-invisible'}>
          <DataGrid
            id="fundsGrid"
            ref={this.fundsGridRef}
            dataSource={this.fundsDataSource()}
            {...appConst.defaultGridOptions}
            onRowRemoving={this.attachmentsGridOnRowRemoving}
            onEditorPreparing={this.onEditorPreparingFundsGrid}
          >
            <Editing
              mode="row"
              allowAdding
              allowDeleting
              allowUpdating
            />
            <Column
              dataField="FundDate"
              dataType="date"
              caption="Fund Date"
              format={appConst.dateDisplayFormat}
              sortOrder="desc"
              sortIndex="0"
            />
            <Column dataField="FundTypeId" dataType="number" caption="Type" editCellRender={this.selectBoxFundTypeColumnTemplate}>
              <Lookup dataSource={fundTypesData.store()} valueExpr="Value" displayExpr="Text" />
            </Column>
            <Column dataField="FundAmount" dataType="number" caption="Amount" format={appConst.currencyFormat} />
          </DataGrid>
        </div>
        <div className={(selectedTabIndex === 5) ? 'tab-form-container-visible' : 'tab-container-invisible'}>
          <PersonCallLogGrid
            ref={this.personCallLogGridRef}
            Id={rowData.Id}
          />
        </div>
        <div className={(selectedTabIndex === 6) ? 'tab-form-container-visible' : 'tab-container-invisible'}>
          <PersonCallLogGrid
            ref={this.involvedCallsGridRef}
            Id={rowData.Id}
            isInvolvedCalls
          />
        </div>
      </>
    );
  }

  // This function is to remove the school when trash button is clicked.
  removeSchool(index) {
    const result = confirm(appConst.confirmDialogContent, appConst.confirmDeleteDialogTitle);
    result.then((dialogResult) => {
      if (dialogResult) {
        let { Schools } = this.state;
        let tmpState = [...Schools];
        tmpState.splice(index, 1);
        tmpState = tmpState.map((item, i) => ({ ...item, Index: i }));
        this.setState({ Schools: tmpState });
      }
    });
  }

  // To remove the CSR role of a member.
  removeCSR(saIndex, index) {
    // Confirm dialog.
    const result = confirm(appConst.confirmDialogContent, appConst.confirmDeleteDialogTitle);
    result.then((dialogResult) => {
      if (dialogResult) {
        const { Schools } = this.state;
        let tmp = [...Schools];
        tmp[saIndex].CSRRoleMembers.splice(index, 1);
        tmp[saIndex].CSRRoleMembers = tmp[saIndex].CSRRoleMembers.map((item, i) => ({ ...item, Index: i }));
        this.setState({ Schools: tmp });
        this.schoolsAccordionRef.current.instance().option('selectedIndex', saIndex);
      }
    });
  }

  // To add a new CSR role.
  addCSRRole(index) {
    const { Schools } = this.state;
    const tmpState = [...Schools];
    // We create a new CSRRoleMembers array when the school is new.
    if (typeof (tmpState[index].CSRRoleMembers) === 'undefined') {
      tmpState[index].CSRRoleMembers = [];
    }
    tmpState[index].CSRRoleMembers.push({ Index: tmpState[index].CSRRoleMembers.length });
    this.setState({ Schools: tmpState });
    this.schoolsAccordionRef.current.instance().option('selectedIndex', index);
  }
}

EditMemberForm.propTypes = {
  gendersData: PropTypes.object.isRequired,
  pronounsData: PropTypes.object.isRequired,
  salutationsData: PropTypes.object.isRequired,
  designationsData: PropTypes.object.isRequired,
  emailAddressTypesData: PropTypes.array.isRequired,
  phoneTypesData: PropTypes.array.isRequired,
  contractTypesData: PropTypes.object.isRequired,
  employmentStatusesData: PropTypes.object.isRequired,
  membershipTypesData: PropTypes.object.isRequired,
  rowData: PropTypes.object.isRequired,
  addNewMode: PropTypes.bool.isRequired,
  hideEditPopupAndRefreshGrid: PropTypes.func.isRequired,
  hideEditPopup: PropTypes.func.isRequired,
};
